import React, { useState, useCallback, useEffect, memo } from "react";
import { Box, Tabs, Tab, Grid, Typography, TextField, Button, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, useMediaQuery, useTheme } from "@mui/material";
import { Person as PersonIcon, Cake as CakeIcon, Wc as WcIcon, Email as EmailIcon, Phone as PhoneIcon, Home as HomeIcon, Domain as DomainIcon, Groups as GroupsIcon, LocationOn as LocationOnIcon, Save as SaveIcon, Edit as EditIcon, BusinessCenter as BusinessCenterIcon, SupervisorAccount as ManagerIcon, Badge as RoleIcon, Apartment as OrgIcon, EventAvailable as DateIcon, Bloodtype as BloodtypeIcon } from "@mui/icons-material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from "dayjs";
import { getData, putData } from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/endpoints";
import { formatDate } from "../../utils/dateFormat";

const GENDERS = ["Male", "Female", "Other"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const EditableField = memo(({ label, value, icon: Icon, onChange, isEditing, type = "text", options = [], multiline = false, readOnly = false, error = false, helperText = "" }) => {
    const effectivelyEditing = isEditing && !readOnly;

    const renderInput = () => {
        if (type === "date") {
            return (
                <DatePicker
                    label={label}
                    format="DD/MM/YYYY"
                    value={value ? dayjs(value) : null}
                    disableFuture
                    onChange={(newValue) => onChange(newValue ? newValue.format('YYYY-MM-DD') : "")}
                    slotProps={{
                        textField: {
                            size: 'small',
                            fullWidth: true,
                            variant: "standard",
                            placeholder: `Select ${label}`,
                            sx: {
                                '& .MuiInputBase-input': { fontSize: { xs: '0.8rem', sm: '1rem' } },
                                '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '1rem' } }
                            }
                        }
                    }}
                />
            );
        }

        return (
            <TextField
                fullWidth
                label={label}
                select={type === "select"}
                variant="standard"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                size="small"
                multiline={multiline}
                rows={multiline ? 3 : 1}
                placeholder={`Enter ${label}`}
                InputLabelProps={{ shrink: true }}
                error={error}
                helperText={helperText}
                sx={{
                    '& .MuiInputBase-input': { fontSize: { xs: '0.8rem', sm: '1rem' } },
                    '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '1rem' } },
                    '& .MuiFormHelperText-root': { fontSize: { xs: '0.65rem', sm: '0.75rem' } }
                }}
            >
                {type === "select" && options.map((opt) => (
                    <MenuItem key={opt} value={opt} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>{opt}</MenuItem>
                ))}
            </TextField>
        );
    };

    return (
        <Box sx={{ mb: 3, display: 'flex', alignItems: multiline && !effectivelyEditing ? 'flex-start' : 'center' }}>
            <Icon sx={{ color: '#5f6368', mr: 2, fontSize: { xs: 18, sm: 22 }, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                {!effectivelyEditing && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                        {label}
                    </Typography>
                )}
                {effectivelyEditing ? renderInput() : (
                    <Typography variant="body1" sx={{ fontWeight: 500, color: readOnly && isEditing ? '#9aa0a6' : '#202124', fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                        {value || 'Not Provided'}
                    </Typography>
                )}
            </Box>
        </Box>
    );
});

function MyProfilePage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [userData] = useState(() => JSON.parse(localStorage.getItem("userData") || "{}"));
    const [userDetails, setUserDetails] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [isGlobalEditing, setIsGlobalEditing] = useState(false);

    const userRole = userData?.user_type?.toLowerCase();

    // Track only modified fields
    const [changes, setChanges] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});

    // Navigation Protection States
    const [pendingTab, setPendingTab] = useState(null);
    const [showDialog, setShowDialog] = useState(false);

    const isDirty = Object.keys(changes).length > 0;
    const isEditButtonVisible = tabValue !== 2 && userRole !== 'approver';

    const fetchUserDetails = useCallback(async () => {
        if (!userData.id) return;
        try {
            const response = await getData(`${API_ENDPOINTS.fetchUserDetails}?userId=${userData.id}`);
            if (response.status === "SUCCESS") {
                setUserDetails(response.message.user);
                setChanges({});
                setFieldErrors({});
            }
        } catch (error) {
            return;
        }
    }, [userData.id]);

    useEffect(() => {
        fetchUserDetails();
    }, [fetchUserDetails]);

    const handleFieldChange = (field, newValue) => {
        let error = '';

        if (field === 'phone_number' || field === 'emergency_contact') {
            const digits = newValue.replace(/\D/g, "");
            if (digits.length > 10) return;
            newValue = digits;
            if (digits.length > 0 && digits.length < 10) {
                error = 'Must be exactly 10 digits';
            }
        }

        if (field === 'personal_email' && newValue) {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newValue)) {
                error = 'Enter a valid email address';
            }
        }

        setFieldErrors(prev => ({ ...prev, [field]: error }));
        setUserDetails(prev => ({ ...prev, [field]: newValue }));
        setChanges(prev => ({ ...prev, [field]: newValue }));
    };

    const handleSave = async (targetTab = null) => {
        if (Object.values(fieldErrors).some(e => e)) return;

        if (!isDirty) {
            setIsGlobalEditing(false);
            if (targetTab !== null) setTabValue(targetTab);
            return;
        }

        try {
            const response = await putData(API_ENDPOINTS.updateEmployeeProfile(userData.id), changes);

            if (response.status === "SUCCESS") {
                setIsGlobalEditing(false);
                setShowDialog(false);
                if (targetTab !== null) {
                    setTabValue(targetTab);
                }
                // Re-fetch from the server rather than trusting local optimistic state,
                // so any field the backend didn't actually persist is reflected immediately.
                await fetchUserDetails();
            }
        } catch (error) {
            return;
        }
    };

    const handleHeaderButtonClick = () => {
        if (isGlobalEditing) {
            handleSave();
        } else {
            setIsGlobalEditing(true);
        }
    };

    const handleTabChange = (event, newValue) => {
        if (isDirty) {
            setPendingTab(newValue);
            setShowDialog(true);
        } else {
            setTabValue(newValue);
            setIsGlobalEditing(false);
        }
    };

    const confirmDiscard = () => {
        setChanges({});
        setIsGlobalEditing(false);
        setTabValue(pendingTab);
        setShowDialog(false);
        fetchUserDetails(); // Re-fetch to clear unsaved UI changes
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ width: '100%' }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: 1,
                    borderColor: 'divider',
                    pr: { xs: 0, sm: 2 }
                }}>
                    <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                        <Tab label="Personal Details" sx={{ fontWeight: 600, textTransform: 'none', fontSize: { xs: '0.7rem', sm: '0.875rem' }, minWidth: { xs: 'auto', sm: 90 } }} />
                        <Tab label="Contact Information" sx={{ fontWeight: 600, textTransform: 'none', fontSize: { xs: '0.7rem', sm: '0.875rem' }, minWidth: { xs: 'auto', sm: 90 } }} />
                        {userRole !== 'approver' && <Tab label="Work Details" sx={{ fontWeight: 600, textTransform: 'none', fontSize: { xs: '0.7rem', sm: '0.875rem' }, minWidth: { xs: 'auto', sm: 90 } }} />}
                    </Tabs>

                    {isEditButtonVisible && (
                        <Button
                            variant={isGlobalEditing ? "contained" : "outlined"}
                            startIcon={isGlobalEditing ? <SaveIcon /> : <EditIcon />}
                            size="small"
                            sx={{
                                display: { xs: 'none', sm: 'inline-flex' },
                                textTransform: 'none', borderRadius: '20px', fontWeight: 600,
                                my: 1, fontSize: '0.8125rem', px: 2
                            }}
                            onClick={handleHeaderButtonClick}
                        >
                            {isGlobalEditing ? "Save Changes" : tabValue === 0 ? "Edit Personal" : "Edit Contact"}
                        </Button>
                    )}
                </Box>

                {isEditButtonVisible && (
                    <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'flex-end', px: 2, py: 1 }}>
                        <Button
                            variant={isGlobalEditing ? "contained" : "outlined"}
                            startIcon={isGlobalEditing ? <SaveIcon /> : <EditIcon />}
                            size="small"
                            sx={{ textTransform: 'none', borderRadius: '20px', fontWeight: 600, fontSize: '0.65rem', px: 1 }}
                            onClick={handleHeaderButtonClick}
                        >
                            {isGlobalEditing ? "Save Changes" : tabValue === 0 ? "Edit Personal" : "Edit Contact"}
                        </Button>
                    </Box>
                )}

                {/* Tab 0: Personal */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <EditableField label="First Name" value={userDetails?.first_name} icon={PersonIcon} isEditing={isGlobalEditing} onChange={(v) => handleFieldChange('first_name', v)} />
                            <EditableField label="Last Name" value={userDetails?.last_name} icon={PersonIcon} isEditing={isGlobalEditing} onChange={(v) => handleFieldChange('last_name', v)} />
                            <EditableField label="Date of Birth" value={formatDate(userDetails?.dob)} icon={CakeIcon} type="date" isEditing={isGlobalEditing} onChange={(v) => handleFieldChange('dob', v)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <EditableField label="Gender" value={userDetails?.gender} icon={WcIcon} type="select" options={GENDERS} isEditing={isGlobalEditing} onChange={(v) => handleFieldChange('gender', v)} />
                            <EditableField label="Blood Group" value={userDetails?.blood_group} icon={BloodtypeIcon} type="select" options={BLOOD_GROUPS} isEditing={isGlobalEditing} onChange={(v) => handleFieldChange('blood_group', v)} />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 1: Contact */}
                <TabPanel value={tabValue} index={1}>
                    <Grid container spacing={4}>
                        {/* Left Column: Basic Contact */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <EditableField
                                label="Work Email"
                                value={userDetails?.email}
                                icon={EmailIcon}
                                readOnly
                                isEditing={isGlobalEditing}
                            />
                            <EditableField
                                label="Personal Email"
                                value={userDetails?.personal_email}
                                icon={EmailIcon}
                                isEditing={isGlobalEditing}
                                onChange={(v) => handleFieldChange('personal_email', v)}
                                error={!!fieldErrors.personal_email}
                                helperText={fieldErrors.personal_email || ''}
                            />
                            <EditableField
                                label="Phone Number"
                                value={userDetails?.phone_number}
                                icon={PhoneIcon}
                                isEditing={isGlobalEditing}
                                onChange={(v) => handleFieldChange('phone_number', v)}
                                error={!!fieldErrors.phone_number}
                                helperText={fieldErrors.phone_number || ''}
                            />
                            <EditableField
                                label="Address"
                                value={
                                    typeof userDetails?.addresses?.[0] === 'object'
                                        ? [userDetails.addresses[0].address_line, userDetails.addresses[0].city, userDetails.addresses[0].state, userDetails.addresses[0].country].filter(Boolean).join(', ')
                                        : userDetails?.addresses?.[0] || ""
                                }
                                icon={HomeIcon}
                                multiline
                                readOnly
                                isEditing={isGlobalEditing}
                                onChange={(v) => handleFieldChange('addresses', [v])}
                            />
                        </Grid>

                        {/* Right Column: Emergency Contact */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <EditableField
                                label="Emergency Contact"
                                value={userDetails?.emergency_contact}
                                icon={PhoneIcon}
                                isEditing={isGlobalEditing}
                                onChange={(v) => handleFieldChange('emergency_contact', v)}
                                error={!!fieldErrors.emergency_contact}
                                helperText={fieldErrors.emergency_contact || ''}
                            />
                            <EditableField
                                label="Emergency Contact Relationship"
                                value={userDetails?.emergency_contact_relationship}
                                icon={GroupsIcon}
                                type="select"
                                options={["Parent", "Spouse", "Sibling", "Friend", "Other"]}
                                isEditing={isGlobalEditing}
                                onChange={(v) => handleFieldChange('emergency_contact_relationship', v)}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 2: Work (Read Only) */}
                <TabPanel value={tabValue} index={2}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <EditableField label="Employee ID" value={userDetails?.emp_id} icon={BusinessCenterIcon} readOnly isEditing={isGlobalEditing} />
                            <EditableField label="Organization" value={userDetails?.organization_name} icon={OrgIcon} readOnly isEditing={isGlobalEditing} />
                            <EditableField label="Department" value={userDetails?.department_name} icon={DomainIcon} readOnly isEditing={isGlobalEditing} />
                            <EditableField label="Team" value={userDetails?.team_name} icon={GroupsIcon} readOnly isEditing={isGlobalEditing} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <EditableField label="Role / Designation" value={userDetails?.designation_name} icon={RoleIcon} readOnly isEditing={isGlobalEditing} />
                            <EditableField label="Manager" value={userDetails?.report_manager ? `${userDetails.report_manager.first_name} ${userDetails.report_manager.last_name || ''}`.trim() : 'Not Assigned'} icon={ManagerIcon} readOnly isEditing={isGlobalEditing} />
                            <EditableField label="Work Location" value={userDetails?.location_name} icon={LocationOnIcon} readOnly isEditing={isGlobalEditing} />
                            <EditableField label="Date of Joining" value={userDetails?.doj ? dayjs(userDetails.doj).format('DD/MM/YYYY') : ''} icon={DateIcon} readOnly isEditing={isGlobalEditing} />
                        </Grid>
                    </Grid>
                </TabPanel>

                <Dialog
                    open={showDialog}
                    onClose={() => setShowDialog(false)}
                    fullWidth
                    fullScreen={isMobile}
                    PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 } } }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Unsaved Changes</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                            You have modified fields on this tab. How would you like to proceed?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={confirmDiscard} color="error" sx={{ textTransform: 'none', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            Discard Changes
                        </Button>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button onClick={() => setShowDialog(false)} sx={{ textTransform: 'none', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            Cancel
                        </Button>
                        <Button onClick={() => handleSave(pendingTab)} variant="contained" sx={{ textTransform: 'none', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            Save & Switch
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
}

export default MyProfilePage;